import dynamicfunction from '../../utils/dynamicfunction';
import Text from '../Text';
import { fontSizes } from '../../constants/constants';
import ViewReverse from '../ViewReverse';
import { cn, getContentKeyAccToLang } from '../../utils/utils';
import CountryList from 'country-list-with-dial-code-and-flag';
import Dropdown from './Dropdown';
import FlagComponent from '../FlagComponent';
import { isValidPhoneNumber, getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import { useSelector } from 'react-redux';
import { getRejectedFieldData, getSectionRejectedFields, isRequiredEmpty } from '../../utils/functions';
import ErrorText from './ErrorText';
import { ReactSVG } from 'react-svg';
import icons from '../../constants/icons';

const PhoneInput = ({
  value,
  onChange,
  placeholder,
  className,
  label,
  labelColor = 'text-primary-light',
  divClasses,
  isDisabled = false,
  isRequired = true,
  country,
  setCountry,
  remarksMetadata = null
}) => {
  const { TextWeight, TextAlign, isArabic } = dynamicfunction();
  
  const dashboardContent = useSelector((state) => state.content?.dashboardContent);
  const { rejection_summary, is_rejected, field_codes } = useSelector((state) => state.corporate);

  const getPlaceholderKey = (key) => {
    return getContentKeyAccToLang(dashboardContent, key, isArabic);
  };

  const handleChange = (e) => {
    const isValid = isValidPhoneNumber(`${country?.dial_code}${e.target.value}`, country?.code);
    onChange(e.target.value, isValid);
  };

  const handleChangeCountry = (country) => {
    setCountry?.(country);
    onChange('', false);
  };

  const getMaxLengthByCountry = (countryCode) => {
    try {
      const example = getExampleNumber(countryCode, examples);
      return example?.nationalNumber?.length || 15; // fallback
    } catch {
      return 15;
    }
  };

  const all_countries = CountryList.getAll().map((country) => country?.data);
  // keep Saudi Arabia as the first option, rest in their original order
  const countries = [
    ...all_countries.filter((item) => item?.code === 'SA'),
    ...all_countries.filter((item) => item?.code !== 'SA'),
  ];
  const maxLength = getMaxLengthByCountry(country?.code);

  const rejectedFields = getSectionRejectedFields(rejection_summary, remarksMetadata?.sectionId);
  const rejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, remarksMetadata?.key);
  const isFieldDisabled = isDisabled || (is_rejected && remarksMetadata && (!rejectedField?.remarks || rejectedField?.remarks?.length < 1));
const country_code = country?.dial_code?.[0] === "+" ? country?.dial_code : `+${country?.dial_code}`

  return (
    <div className={cn('w-full ', divClasses)}>
      {label && (
        <div data-required-empty={isRequiredEmpty(isRequired, label, value)}>
          <Text text={label} size={fontSizes.h14} color={labelColor} className={`mb-1`} isRequired={isRequired} isTruncate={false} />
        </div>
      )}

      <ViewReverse className={cn('gap-2')}>
        <Dropdown
          className={cn('min-w-[100px] max-w-[100px]')}
          isSearch
          isPhone
          menuClasses={className}
          valueKey={'name'}
          optionsClasses={cn('w-[280px]', isArabic && 'right-0')}
          options={countries}
          value={country}
          isDisabled={isFieldDisabled}
          onSelect={handleChangeCountry}
          remarksMetadata={remarksMetadata}
          isRemarks={false}
        >
          {
            country ? (
              <ViewReverse className={'gap-2'}>
                <FlagComponent code={country?.code} />
                <Text text={country_code} color={rejectedField?.remarks?.length > 0 ? 'text-red-600' : 'text-black'} isTruncate={false} />
              </ViewReverse>
            ) : <Text text={getPlaceholderKey('select')} />
          }
        </Dropdown>
        <div
          className={cn(
            'px-3 py-1.5 border border-grey rounded w-full h-[40px] items-center',
            className,
            (isFieldDisabled || !country) && 'cursor-not-allowed bg-disabled',
            rejectedField?.remarks?.length > 0 && 'border-red-600',
          )}
        >
          <input
            type={'tel'}
            value={value}
            onChange={handleChange}
            disabled={isFieldDisabled || !country}
            placeholder={placeholder}
            className={cn(
              TextWeight.regular,
              TextAlign,
              'outline-none w-full text-sm bg-transparent',
              (isFieldDisabled || !country) && !value ? 'text-dark-grey/50 cursor-not-allowed' : 'text-black',
              (isFieldDisabled || !country) && 'cursor-not-allowed',
              rejectedField?.remarks?.length > 0 && 'text-red-600',
            )}
            maxLength={maxLength}
          />
        </div>
      </ViewReverse>
      {
        rejectedField?.remarks?.length > 0 && (
          <ViewReverse className='items-center gap-2 mt-2'>
            <ReactSVG src={icons.field_info_outline} />
            <ErrorText text={rejectedField?.remarks} className={'mt-0'} />
          </ViewReverse>
        )
      }
    </div>
  );
};

export default PhoneInput;
